/**
 * Print a self-contained HTML document through a hidden iframe, so receipts
 * and labels get their own @page size without disturbing the app. With
 * Chrome started as `--kiosk-printing` this prints silently to the default
 * printer (see README: receipt printers).
 */
export function printHtml(html: string, opts: { title?: string } = {}): Promise<void> {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument!;
    doc.open();
    doc.write(html);
    doc.close();
    if (opts.title) doc.title = opts.title;
    const go = () => {
      try {
        iframe.contentWindow!.focus();
        iframe.contentWindow!.print();
      } finally {
        setTimeout(() => { iframe.remove(); resolve(); }, 1000);
      }
    };
    // Give SVG barcodes and fonts a moment to lay out.
    if (doc.readyState === 'complete') setTimeout(go, 150);
    else iframe.onload = () => setTimeout(go, 150);
  });
}

export function escapeHtml(s: unknown): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

/** Download text (TSPL/ZPL printer commands) as a file. */
export function downloadText(filename: string, text: string, mime = 'text/plain') {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function downloadBlob(filename: string, data: BlobPart, mime: string) {
  const url = URL.createObjectURL(new Blob([data], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
